import { createAsyncThunk } from "@reduxjs/toolkit";
import { WaterCharacteristicsPreference } from "@src/core/api/waterCharacteristicsPreferences";
import { BatchSelectionResponse } from "@src/core/engine/workers/batchSelection/schema";
import { RootState } from "@src/core/store";
import { BuildSuggestionOutput } from "@src/routes/projects/$projectId/selections/hooks/useBuildSuggestionOutput";
import { GetGroupedProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { UnitConversionFn } from "@src/shared/hooks/useUnitConversion";
import { list, objectify, sift } from "radashi";
import { EMPTY_CELL_ID } from "../components/BatchSelectionDataTable/hooks/useBatchSelectionDataTableColumns";
import { FetchSuggestions } from "../contexts/BatchSuggestionsContext";
import { BatchSelectionState } from "./batchSelectionSlice";
import { buildInput } from "./helpers/buildInput";
import { ensureDataState } from "./helpers/ensureDataState";
import { getEmptyRow } from "./helpers/getEmptyRow";
import { matchesRequest } from "./helpers/matchesRequest";
import { processRowUpdate } from "./helpers/processRowUpdate";
import { BatchSelectionRow } from "./types";

type PasteCellValuesArgs = {
  buildSuggestionOutput: BuildSuggestionOutput;
  categoryId: string;
  columnId: keyof BatchSelectionRow;
  columnOrder: string[];
  convertToInternationalUnit: UnitConversionFn;
  convertToUserUnit: UnitConversionFn;
  fetchSuggestions: FetchSuggestions;
  getProductDescriptions: GetGroupedProductDescriptions;
  startIndex: number;
  useFlowOrPower: string;
  values: (number | string)[][];
  waitUntilReady: () => Promise<void>;
  waterCharacteristics: WaterCharacteristicsPreference | null;
};

export const initPasteCellValue = (
  state: BatchSelectionState,
  args: PasteCellValuesArgs,
  requestId: string,
) => {
  const { columnId, columnOrder, startIndex, values } = args;
  const blankColumnIndex = columnOrder.indexOf(EMPTY_CELL_ID);
  const inputsColumns = columnOrder.slice(0, blankColumnIndex);
  const startColIndex = inputsColumns.indexOf(columnId);
  if (startColIndex === -1 || values.length === 0) return;

  const nextData = [...state.data];
  const lastRowIndex = startIndex + values.length - 1;
  if (lastRowIndex >= nextData.length) {
    nextData.push(...list(nextData.length, lastRowIndex, getEmptyRow));
  }

  values.forEach((rowValues, rowOffset) => {
    const rowIndex = startIndex + rowOffset;
    const updates = objectify(
      sift(
        rowValues.map((value, colOffset) => {
          const targetColumnId = inputsColumns[startColIndex + colOffset];
          return targetColumnId ? { targetColumnId, value } : null;
        }),
      ),
      (entry) => entry.targetColumnId,
      (entry) => entry.value,
    );

    const prevData = nextData[rowIndex];
    nextData[rowIndex] = processRowUpdate({
      correlationId: requestId,
      prevRow: prevData,
      row: { ...prevData, ...updates },
      ...args,
    });
  });
  state.data = ensureDataState(nextData);
};

export const pasteCellValues = createAsyncThunk<
  Omit<BatchSelectionResponse, "type">,
  PasteCellValuesArgs,
  { state: RootState }
>(
  "batchSelection/pasteCellValues",
  async (
    {
      categoryId,
      convertToInternationalUnit,
      fetchSuggestions,
      startIndex,
      values,
      waitUntilReady,
    },
    { getState, requestId },
  ) => {
    await waitUntilReady();
    const items = getState()
      .batchSelection.data.slice(startIndex, startIndex + values.length)
      .filter((row) => matchesRequest(row, requestId));
    if (items.length === 0) return { correlationId: requestId, results: [] };

    return fetchSuggestions({
      categoryId,
      correlationId: requestId,
      inputs: items.map((item) => buildInput(item, convertToInternationalUnit)),
    });
  },
);
